import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  ShoppingCart,
  ExternalLink,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Copy,
  MapPin,
  DollarSign,
  RefreshCw
} from 'lucide-react';
import { api, PurchaseOrder, ReadyGift, ShippingAddress } from '../lib/api';
import { toast } from 'sonner';

interface PurchaseManagementProps {
  onNavigate: (screen: string, data?: any) => void;
  giftGroupId?: string;
}

export function PurchaseManagement({ onNavigate, giftGroupId }: PurchaseManagementProps) {
  const [pendingPurchases, setPendingPurchases] = useState<PurchaseOrder[]>([]);
  const [readyGifts, setReadyGifts] = useState<ReadyGift[]>([]);
  const [giftGroupInfo, setGiftGroupInfo] = useState<{
    id: string;
    title: string;
    giftDate: string;
    orderByDate: string;
    totalPool: number;
    remainingPool: number;
  } | null>(null);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: '',
    vendorOrderId: '',
    trackingNumber: '',
    carrier: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [giftGroupId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (giftGroupId) {
        // Load ready purchases for specific gift group
        const response = await api.getReadyPurchases(giftGroupId);
        setGiftGroupInfo(response.giftGroup);
        setShippingAddress(response.shippingAddress);
        setReadyGifts(response.readyToPurchase);
      } else {
        // Load all pending purchases (admin view)
        const response = await api.getPendingPurchases();
        setPendingPurchases(response.purchases);
      }
    } catch (error) {
      console.error('Failed to load purchase data:', error);
      toast.error('Failed to load purchase data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePurchaseOrder = async (gift: ReadyGift) => {
    if (!giftGroupId) return;
    
    try {
      await api.createPurchaseOrder(
        giftGroupId,
        gift.id,
        shippingAddress ?? undefined
      );
      
      toast.success('Purchase order created!', {
        description: `Ready to order: ${gift.name}`
      });
      
      // Refresh data
      loadData();
    } catch (error) {
      console.error('Failed to create purchase order:', error);
      toast.error('Failed to create purchase order');
    }
  };

  const handleOpenVendorUrl = (order: PurchaseOrder) => {
    // Open the product URL in a new tab
    window.open(order.product_url, '_blank');
    
    // Show the update dialog to capture order details
    setSelectedOrder(order);
    setUpdateForm({
      status: 'ordered',
      vendorOrderId: '',
      trackingNumber: '',
      carrier: '',
      notes: ''
    });
    setShowUpdateDialog(true);
  };

  const handleUpdateOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      setUpdating(true);
      
      await api.updatePurchaseOrder(selectedOrder.id, {
        status: updateForm.status as any,
        vendorOrderId: updateForm.vendorOrderId || undefined,
        trackingNumber: updateForm.trackingNumber || undefined,
        carrier: updateForm.carrier || undefined,
        notes: updateForm.notes || undefined
      });
      
      toast.success('Order updated successfully');
      setShowUpdateDialog(false);
      loadData();
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order');
    } finally {
      setUpdating(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error(`Failed to copy ${label}`, {
        description: 'Please copy the text manually'
      });
    }
  };

  const formatAddress = (order: PurchaseOrder) => {
    const parts = [
      order.shipping_name,
      order.shipping_street,
      `${order.shipping_city}, ${order.shipping_state} ${order.shipping_postal_code}`,
      order.shipping_country
    ].filter(Boolean);
    return parts.join('\n');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'ordered':
        return <Badge className="bg-blue-500"><Package className="w-3 h-3 mr-1" /> Ordered</Badge>;
      case 'shipped':
        return <Badge className="bg-purple-500"><Truck className="w-3 h-3 mr-1" /> Shipped</Badge>;
      case 'delivered':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Delivered</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
      case 'failed':
        return <Badge className="bg-red-600"><AlertCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getVendorDisplayName = (vendor: string) => {
    const vendors: Record<string, string> = {
      amazon: 'Amazon',
      walmart: 'Walmart',
      target: 'Target',
      bestbuy: 'Best Buy',
      etsy: 'Etsy',
      ebay: 'eBay',
      unknown: 'Other'
    };
    return vendors[vendor] || vendor;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading purchase data...</p>
          </div>
        </Card>
      </div>
    );
  }

  // Gift Group specific view - show gifts ready to purchase
  if (giftGroupId && giftGroupInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 p-4">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <Card className="p-6 border border-primary/20 shadow-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <ShoppingCart className="w-6 h-6 text-primary" />
                  <h1 className="text-primary">Purchase Gifts</h1>
                </div>
                <p className="text-muted-foreground">{giftGroupInfo.title}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Gift Pool Available</div>
                <div className="text-2xl text-green-600">${giftGroupInfo.totalPool?.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground">
                  Gift Date: {formatDate(giftGroupInfo.giftDate)}
                </div>
              </div>
            </div>
          </Card>

          {/* Shipping Address */}
          {shippingAddress && (
            <Card className="p-6 border border-primary/20 shadow-lg">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-primary mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg mb-2">Shipping Address</h3>
                  <div className="text-sm text-muted-foreground">
                    <p>{shippingAddress.name}</p>
                    <p>{shippingAddress.street}</p>
                    <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postalCode}</p>
                    <p>{shippingAddress.country}</p>
                    {shippingAddress.phone && <p>Phone: {shippingAddress.phone}</p>}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(
                    `${shippingAddress.name}\n${shippingAddress.street}\n${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.postalCode}\n${shippingAddress.country}`,
                    'Address'
                  )}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
            </Card>
          )}

          {/* Ready to Purchase Gifts */}
          <Card className="border border-primary/20 shadow-lg">
            <div className="p-6 border-b border-primary/20">
              <h2 className="text-primary">Gifts Ready to Purchase</h2>
              <p className="text-sm text-muted-foreground mt-1">
                These gifts are within budget and ranked highest by the group
              </p>
            </div>

            {readyGifts.length === 0 ? (
              <div className="p-12 text-center">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No gifts ready to purchase</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Either the pool is empty or no gifts fit within the budget
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Gift</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {readyGifts.map((gift, index) => (
                    <TableRow key={gift.id} className="bg-green-50">
                      <TableCell>
                        <Badge className="bg-primary">{index + 1}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-sm">
                          <p className="font-medium">{gift.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{gift.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getVendorDisplayName(gift.vendor_type || 'unknown')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">${gift.totalCost?.toFixed(2)}</p>
                          {gift.shipping_cost > 0 && (
                            <p className="text-xs text-muted-foreground">
                              +${gift.shipping_cost?.toFixed(2)} shipping
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleCreatePurchaseOrder(gift)}
                            className="bg-primary"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Create Order
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(gift.url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => onNavigate('gift-group-overview', { giftGroupId })}>
              Back to Gift Group
            </Button>
            <Button variant="outline" onClick={() => onNavigate('admin')}>
              Admin Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Admin view - show all pending purchases
  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card className="p-6 border border-primary/20 shadow-lg">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <ShoppingCart className="w-6 h-6 text-primary" />
                <h1 className="text-primary">Purchase Management</h1>
              </div>
              <p className="text-muted-foreground">Manage orders for all gift groups</p>
            </div>
            <Button onClick={loadData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </Card>

        {/* Instructions */}
        <Alert>
          <ShoppingCart className="h-4 w-4" />
          <AlertDescription>
            <strong>Non-API Purchasing Flow:</strong> Click "Order Now" to open the vendor website. 
            After placing the order manually, enter the vendor order ID and any tracking information.
          </AlertDescription>
        </Alert>

        {/* Pending Orders Table */}
        <Card className="border border-primary/20 shadow-lg">
          <div className="p-6 border-b border-primary/20">
            <h2 className="text-primary">Active Orders</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {pendingPurchases.length} order(s) requiring attention
            </p>
          </div>

          {pendingPurchases.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-muted-foreground">All orders are complete!</p>
              <p className="text-sm text-muted-foreground mt-2">
                No pending, ordered, or shipped orders to manage
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Gift Group</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Shipping To</TableHead>
                    <TableHead>Order Info</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingPurchases.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.gift_group_title}</p>
                          <p className="text-xs text-muted-foreground">
                            Gift: {formatDate(order.gift_date)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium truncate">{order.product_name}</p>
                          <a
                            href={order.product_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Product
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getVendorDisplayName(order.vendor_name)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{order.total_amount?.toFixed(2)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs text-xs">
                          <p>{order.shipping_name}</p>
                          <p className="text-muted-foreground">
                            {order.shipping_city}, {order.shipping_state}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-0 h-auto text-xs text-blue-600"
                            onClick={() => copyToClipboard(formatAddress(order), 'Address')}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy Address
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          {order.vendor_order_id && (
                            <p>Order #: {order.vendor_order_id}</p>
                          )}
                          {order.tracking_number && (
                            <p>Tracking: {order.tracking_number}</p>
                          )}
                          {order.carrier && (
                            <p>Carrier: {order.carrier}</p>
                          )}
                          {!order.vendor_order_id && !order.tracking_number && (
                            <p className="text-muted-foreground">Not yet ordered</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {order.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => handleOpenVendorUrl(order)}
                              className="bg-primary"
                            >
                              <ShoppingCart className="w-4 h-4 mr-1" />
                              Order Now
                            </Button>
                          )}
                          {order.status !== 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedOrder(order);
                                setUpdateForm({
                                  status: order.status,
                                  vendorOrderId: order.vendor_order_id || '',
                                  trackingNumber: order.tracking_number || '',
                                  carrier: order.carrier || '',
                                  notes: ''
                                });
                                setShowUpdateDialog(true);
                              }}
                            >
                              Update
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => onNavigate('admin')}>
            Back to Admin Dashboard
          </Button>
          <Button variant="outline" onClick={() => onNavigate('dashboard')}>
            User Dashboard
          </Button>
        </div>
      </div>

      {/* Update Order Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
            <DialogDescription>
              {selectedOrder?.product_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={updateForm.status}
                onValueChange={(value) => setUpdateForm({ ...updateForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Vendor Order ID</Label>
              <Input
                placeholder="e.g., 111-2345678-9012345"
                value={updateForm.vendorOrderId}
                onChange={(e) => setUpdateForm({ ...updateForm, vendorOrderId: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Tracking Number</Label>
              <Input
                placeholder="e.g., 1Z999AA10123456784"
                value={updateForm.trackingNumber}
                onChange={(e) => setUpdateForm({ ...updateForm, trackingNumber: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Carrier</Label>
              <Select
                value={updateForm.carrier}
                onValueChange={(value) => setUpdateForm({ ...updateForm, carrier: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="Amazon">Amazon Logistics</SelectItem>
                  <SelectItem value="OnTrac">OnTrac</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes about this order..."
                value={updateForm.notes}
                onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateOrder} disabled={updating}>
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
